import css from './style.module.scss';
import A from '../A';
import Button from '../Button';
import Pill from '../Pill';
import cls from '../../util/cls';
import VariantSelect, { VARIANT_SELECT_FRAGMENT } from '../VariantSelect';
import Modal from '../Modal';
import { useMemo, useState } from 'react';
import Prose from '../Prose';
import { Emit, Events } from '../../util/Events';
import { gql } from '../../util/fetchGql';
import Form from '../Form';
import currency from '../../util/currency';

export const OFFER_FRAGMENT = gql`
	fragment Offer on products_Product {
		title
		price: defaultPriceAsCurrency
		canBookADemo
		canTradeIn
		excerpt
		label
		variants {
			...VariantSelect
			... on products_Variant {
				enquireOnly
				isAvailable
			}
		}
		category: productCategories (limit: 1) {
			title
			uri
		}
		brand: brands (limit: 1) {
			title
			uri
		}
		availableForPurchase
	}
	${VARIANT_SELECT_FRAGMENT}
`;

export const OFFER_MODALS_FRAGMENT = gql`
	fragment OfferModals on Query {
		offerModals: globalSet (handle: "formDescriptions") {
			... on formDescriptions_GlobalSet {
				demoText
				enquireText
				tradeText
			}
		}
	}
`;

export default function Offer ({
	offerModals: { demoText, enquireText, tradeText },
	float = false,
	title,
	price,
	canBookADemo,
	canTradeIn,
	excerpt,
	label,
	variants,
	accent = 'white',
	onVariantChange = () => {},
	activeVariant,
	category: [category],
	brand: [brand],
	availableForPurchase,
}) {
	const [demoOpen, setDemoOpen] = useState(false)
		, [tradeOpen, setTradeOpen] = useState(false)
		, [enquireOpen, setEnquireOpen] = useState(false)
		, [activeVariantId, setActiveVariantId] = useState(variants?.[0]?.id)
		, [optionsAvailable, setOptionsAvailable] = useState(true);

	const variantsById = useMemo(() => {
		return variants.reduce((a, b) => {
			a[b.id] = b;
			return a;
		}, {});
	}, [variants]);

	const _onVariantChange = value => {
		if (value) {
			setOptionsAvailable(true);
			setActiveVariantId(value);
			onVariantChange && onVariantChange(value);
		} else {
			setOptionsAvailable(false);
		}
	};

	const onAddToCart = async () => {
		Emit(Events.AddToCart, activeVariantId);
		Emit(Events.OpenCart);
	};

	const onDemoClick = () => setDemoOpen(true)
		, onCloseDemo = () => setDemoOpen(false)
		, onTradeClick = () => setTradeOpen(true)
		, onCloseTrade = () => setTradeOpen(false)
		, onEnquireClick = () => setEnquireOpen(true)
		, onCloseEnquire = () => setEnquireOpen(false);

	const hasSale = activeVariant?.sale !== activeVariant?.price || activeVariant?.rrp;

	return (
		<div className={cls(css.offer, {
			[css.float]: float,
			[css.blue]: accent === 'blue',
		})}>
			{brand && (
				<div className={css.categories}>
					<A href={brand.uri}>{brand.title}</A>
				</div>
			)}

			{hasSale ? (
				<span className={css.price}>
					{(activeVariant?.sale).replace('.00', '')}
					<del>{(currency(activeVariant?.rrp) ?? activeVariant?.price).replace('.00', '')}</del>
				</span>
			) : (
				<span className={css.price}>{(activeVariant?.price ?? price).replace('.00', '')}</span>
			)}

			<h1>{title}</h1>

			<p>{excerpt}</p>

			{/*<Pill grey>
				0% Finance Available. <A href="#">Terms Apply</A>
			</Pill>*/}

			<VariantSelect
				label={label}
				variants={variants}
				white={accent === 'blue'}
				onChange={_onVariantChange}
				defaultSelected={activeVariant?.id}
			/>

			<footer className={css.footer}>
				{optionsAvailable ? variantsById[activeVariantId] && (
					<>
						{availableForPurchase && !variantsById[activeVariantId]?.enquireOnly && variantsById[activeVariantId]?.isAvailable ? (
							<Button type="submit" wide blue={accent !== 'blue'} white={accent === 'blue'} onClick={onAddToCart}>Add to basket</Button>
						) : (
							<Button wide blue={accent !== 'blue'} white={accent === 'blue'} onClick={onEnquireClick}>Enquire Now</Button>
						)}
					</>
				) : (
					<Button disabled wide>Options unavailable</Button>
				)}
				{canBookADemo && <Button onClick={onDemoClick} wide={!canTradeIn}>Book a Demo</Button>}
				{canTradeIn && <Button onClick={onTradeClick} outline white={accent === 'blue'} wide={!canBookADemo}>Trade In</Button>}
			</footer>

			<Modal right isOpen={demoOpen} onRequestClose={onCloseDemo}>
				<Prose El="header" small>
					<h2>Book a demo</h2>
					<p>{demoText}</p>
				</Prose>
				<Form
					handle="bookADemo"
					product={title}
				/>
			</Modal>

			<Modal right isOpen={tradeOpen} onRequestClose={onCloseTrade}>
				<Prose El="header" small>
					<h2>Trade in</h2>
					<p>{tradeText}</p>
					<Button disabled wide>Trade In: {title}</Button>
				</Prose>
				<Form
					handle="tradeIn"
					product={title}
				/>
			</Modal>

			<Modal right isOpen={enquireOpen} onRequestClose={onCloseEnquire}>
				<Prose El="header" small>
					<h2>Enquire Now</h2>
					<p>{enquireText}</p>
					<Button disabled wide>Enquire About: {title}</Button>
				</Prose>
				<Form
					handle="enquire"
					product={title}
				/>
			</Modal>
		</div>
	);
}
